import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { GlideResult } from "./glide/useGlide";
import { replaceBefore, replaceRange, type Edit } from "./textpadCaret";
import css from "./TextPad.module.css";

/**
 * Подсказки росчерка TextPad (06.09, владелец): полоса чипов над клавишами
 * (как Gboard) + хост-логика слова — useGlideWords. Вынесено из TextPad.tsx
 * (гигант, рэтчет): хост лишь прокидывает write и рендерит <TextPadSuggest>.
 *
 * Все правки — относительно КАРЕТКИ (06.09, владелец), хвост после неё цел:
 *  onGlide  — head = v[0..start), tail = после буквы, вставленной тапом первой
 *             клавиши (если она вошла — maxLength); результат head + (пробел,
 *             если head кончается буквой/цифрой) + слово + " " + tail, каретка
 *             после пробела; диапазон слова запоминается;
 *  pick(i)  — чип заменяет слово в диапазоне (регистр первой буквы и хвост
 *             сохраняются), сам становится текущим;
 *  clear    — ✕ убирает слово вместе с пробелом после него;
 *  hide     — любой обычный ввод (add/del) прячет полосу;
 *  longChar — лонгпресс «е» → «ё»: заменяет символ ПЕРЕД кареткой (регистр как был).
 */
type Write = (f: Edit) => void;

interface Words { words: string[]; cur: number; start: number; end: number; upper: boolean }

const cap = (w: string, upper: boolean) => (upper ? w.charAt(0).toUpperCase() + w.slice(1) : w);

export function useGlideWords(write: Write, getMax: () => number) {
  const [st, setSt] = useState<Words | null>(null);
  const ref = useRef<Words | null>(null);
  const set = (s: Words | null) => { ref.current = s; setSt(s); };

  const onGlide = (r: GlideResult) => {
    const words = r.candidates.map((c) => c.word).filter(Boolean);
    const first = words[0];
    if (!first) return;
    const max = getMax();
    let start = r.start, end = start, done = false;
    write((v, caret) => {
      const s = Math.max(0, Math.min(r.start, v.length));
      const hasLetter = !!r.pressed && v.charAt(s).toLowerCase() === r.pressed;
      const head = v.slice(0, s);
      const sep = /[\p{L}\p{N}]$/u.test(head) ? " " : "";
      const w = cap(first, r.upper);
      const out = replaceRange(v, caret, s, s + (hasLetter ? 1 : 0), sep + w + " ", max);
      const ins = out.v.length - (v.length - (hasLetter ? 1 : 0)); /* сколько реально вошло (лимит) */
      start = s + Math.min(sep.length, ins);
      end = Math.min(start + w.length, s + ins);
      done = ins > 0 && end > start;
      if (!done) return null; /* лимит maxLength: слово не вошло — чипы не показываем */
      return { v: out.v, caret: s + ins };
    });
    if (!done) return;
    set({ words, cur: 0, start, end, upper: r.upper });
  };

  const pick = (i: number) => {
    const s = ref.current, raw = s?.words[i];
    if (!s || !raw) return;
    const w = cap(raw, s.upper), max = getMax();
    let end = s.end;
    write((v, caret) => { const out = replaceRange(v, caret, s.start, s.end, w, max); end = s.start + (out.v.length - (v.length - (s.end - s.start))); return out; });
    set({ ...s, cur: i, end });
  };

  const clear = () => {
    const s = ref.current;
    if (!s) return;
    write((v, caret) => replaceRange(v, caret, s.start, v.charAt(s.end) === " " ? s.end + 1 : s.end, ""));
    set(null);
  };

  const hide = () => { if (ref.current) set(null); };

  const longChar = (base: string, alt: string) => {
    write((v, caret) => replaceBefore(v, caret, base, alt));
    hide();
  };

  const chips = st ? st.words.slice(0, 4).map((w) => cap(w, st.upper)) : [];
  return { chips, cur: st?.cur ?? -1, onGlide, pick, clear, hide, longChar };
}

interface SuggestProps {
  chips: string[];
  cur: number;
  onPick: (i: number) => void;
  onClear: () => void;
}

/* Чипы срабатывают на pointerdown (как клавиши): preventDefault — без фокуса
   и хвостового click; ✕ — убрать слово. Пусто = полоса не рендерится. */
export function TextPadSuggest({ chips, cur, onPick, onClear }: SuggestProps) {
  if (!chips.length) return null;
  const tap = (fn: () => void) => (e: ReactPointerEvent<HTMLButtonElement>) => { e.preventDefault(); fn(); };
  return (
    <div className={css.sugg} role="listbox" aria-label="Варианты слова">
      {chips.map((w, i) => (
        <button
          key={`${i}-${w}`}
          type="button"
          role="option"
          aria-selected={i === cur}
          className={`${css.sugChip} ${i === cur ? css.on : ""}`}
          onPointerDown={tap(() => onPick(i))}
        >
          {w}
        </button>
      ))}
      <button type="button" className={css.sugX} aria-label="Убрать слово" onPointerDown={tap(onClear)}>✕</button>
    </div>
  );
}
